import { useEffect, useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useCurrentUser } from '@/react/hooks/useUser';

import { FormSection } from '@@/form-components/FormSection';

import { useHelmChartList } from '../helmChartSourceQueries/useHelmChartList';
import { Chart } from '../types';
import {
  HelmRegistrySelect,
  RegistryValue,
} from '../components/HelmRegistrySelect';
import { useHelmRegistryRepoOptions } from '../helmChartSourceQueries/useHelmRegistries';
import { useIsRegistryAvailable } from '../HelmApplicationView/ChartActions/upgrade/utils/useIsRegistryAvailable';

import { HelmInstallForm } from './HelmInstallForm';
import { HelmTemplatesSelectedItem } from './HelmTemplatesSelectedItem';
import { HelmTemplatesList } from './HelmTemplatesList';

interface Props {
  onSelectHelmChart: (chartName: string) => void;
  namespace?: string;
  name?: string;
  setIsNamespaceDisabled: (isDisabled: boolean) => void;
}

export function HelmTemplates({
  onSelectHelmChart,
  namespace,
  name,
  setIsNamespaceDisabled,
}: Props) {
  const environmentId = useEnvironmentId();
  const [selectedChart, setSelectedChart] = useState<Chart | null>(null);
  const [selectedRegistry, setSelectedRegistry] =
    useState<RegistryValue | null>(null);
  const { user } = useCurrentUser();
  const isOCI = selectedRegistry?.registryId !== undefined;
  const chartListQuery = useHelmChartList(
    user.Id,
    environmentId,
    {
      id: selectedRegistry?.registryId,
      url: selectedRegistry?.registryUrl,
    },
    isOCI
  );
  const registryOptionsQuery = useHelmRegistryRepoOptions(
    environmentId,
    namespace
  );

  const isRegistryAvailable = useIsRegistryAvailable(
    selectedRegistry,
    registryOptionsQuery.registryOptions
  );

  // avoid changing namespaces after selecting a chart, in case the registry is not available in the new namespace
  useEffect(() => {
    setIsNamespaceDisabled(!!selectedChart && !!selectedRegistry?.isOCI);
  }, [selectedChart, selectedRegistry, setIsNamespaceDisabled]);

  return (
    <div className="row">
      <div className="col-sm-12 p-0">
        <FormSection title="Helm chart">
          {selectedChart ? (
            <>
              <HelmTemplatesSelectedItem
                selectedChart={selectedChart}
                clearHelmChart={clearHelmChart}
              />
              <HelmInstallForm
                selectedChart={selectedChart}
                selectedRegistry={selectedRegistry}
                namespace={namespace}
                name={name}
                isRegistryAvailable={isRegistryAvailable}
              />
            </>
          ) : (
            <>
              <HelmRegistrySelect
                selectedRegistry={selectedRegistry}
                onRegistryChange={setSelectedRegistry}
                namespace={namespace}
                isRegistryAvailable={isRegistryAvailable}
                isLoading={registryOptionsQuery.isLoading}
                isLoadingError={registryOptionsQuery.isError}
                registryOptions={registryOptionsQuery.registryOptions}
              />
              {selectedRegistry && (
                <HelmTemplatesList
                  charts={chartListQuery.data ?? []}
                  selectAction={handleChartSelection}
                  isLoadingCharts={chartListQuery.isInitialLoading}
                  selectedRegistry={selectedRegistry}
                />
              )}
            </>
          )}
        </FormSection>
      </div>
    </div>
  );

  function clearHelmChart() {
    setSelectedChart(null);
    onSelectHelmChart('');
    setIsNamespaceDisabled(false);
  }

  function handleChartSelection(chart: Chart) {
    setSelectedChart(chart);
    onSelectHelmChart(chart.name);
  }
}
